/**
 * Responsible for creating a group out of a set of fields and when one of those
 * fields is invalid, all are marked as invalid.
 * @module elements/fields/mirror-field
 */
import $ from 'jquery';
// UNCOMMENT THIS CODE IF BLUR SHOULD BE SUPPORTED
// import find from 'lodash/find';
import registerJQueryPlugin from '../../lib/register-jquery-plugin';

export const EVENT_NAMESPACE = 'mirror-field';

export const EVENTS = {
  VALID: 'valid',
  INVALID: 'invalid',
  FOCUS: 'focus',
  ERROR: 'mirror-field:error',
  RESET: 'mirror-field:reset',
  // UNCOMMENT THIS CODE IF BLUR SHOULD BE SUPPORTED
  // BLUR: 'blur',
  // CHANGE: 'change',
};

const FORM_FIELDS = 'input,textarea,select';

/**
 * Creates an event name with a namespace.
 * @param {string} name - Name of the event
 * @returns {string} Event with namespace
 */
const createEventName = name => `${EVENTS[name.toUpperCase()]}.${EVENT_NAMESPACE}`;

/**
 * Responsible for finding fields based on a group name.
 * @param {Object} fieldMappings - All available field mappings keyed by group
 * name
 * @param {jQuery} $el - Used for looking up the group
 * @returns {Array} All elements within a group
 */
const getFieldList = (fieldMappings, $el) => {
  const name = $el.data('mirrorFieldName');
  const fieldList = fieldMappings[name];
  if (fieldList) {
    return fieldList;
  }

  return [];
};

/**
 * Responsible for appending a field to a group.
 * @param {Object} fieldMappings - All available field mappings keyed by group
 * name
 * @param {jQuery} $el - Element being added to group
 * @returns {Object} - Updated field mappings
 */
const updateFieldMappings = (fieldMappings, $el) => {
  const name = $el.data('mirrorFieldName');
  const fieldList = getFieldList(fieldMappings, $el);
  const updatedFieldList = fieldList.length ? [...fieldList, $el] : [$el];

  return Object.assign({}, fieldMappings, { [name]: updatedFieldList });
};

/**
 * Responsible for receiving a field that is an error and then making all
 * fields associated with the error element invalid.
 * @param {Object} fieldMappings - All available field mappings keyed by group
 * name
 * @param {jQuery} $el - Field applied with error
 */
const applyError = (fieldMappings, $el) => {
  const fieldList = getFieldList(fieldMappings, $el);
  const updatedFieldList = fieldList.filter($field => !$field.is($el));
  const validationMessage = $el.prop('validationMessage');

  updatedFieldList.forEach(($field) => {
    const field = $field[0];
    if ($field.is(FORM_FIELDS)) {
      const eventName = createEventName(EVENTS.INVALID);
      // Remove event so that it doesn't recursively call itself until the
      // call stack goes KABOOM
      $field.off(eventName);
      field.setCustomValidity(validationMessage);
      field.checkValidity();
      // Now that the element has been invalidated, rebind the event for the
      // next invalid event listener.
      $field.on(eventName, () => applyError(fieldMappings, $field));
    }

    const errorEvt = new CustomEvent(EVENTS.ERROR, {
      detail: {
        $field,
        validationMessage,
        $source: $el,
      },
    });
    field.dispatchEvent(errorEvt);
  });
};

/**
 * Responsible for resetting an error on a subset of mirror fields.
 * @param {Object} fieldMappings - All available field mappings keyed by group
 * name
 * @param {jQuery} $el - Field getting error reset
 */
const resetError = (fieldMappings, $el) => {
  const fieldList = getFieldList(fieldMappings, $el);
  fieldList.forEach(($field) => {
    const field = $field[0];
    if ($field.is(FORM_FIELDS)) {
      field.setCustomValidity('');
      field.checkValidity();
      // Only make this available to fields since it plays off of the invalid
      // event which exists in the browser. Technically the invalid event can
      // be fired off on a non-form element, but according to the spec, it's
      // supposed to fire off on a submittable element.
      // https://developer.mozilla.org/en-US/docs/Web/Events/invalid
      const validEvt = new CustomEvent(EVENTS.VALID);
      field.dispatchEvent(validEvt);
    }

    const resetEvt = new CustomEvent(EVENTS.RESET, {
      detail: {
        $field,
        $source: $el,
      },
    });
    field.dispatchEvent(resetEvt);
  });
};

// UNCOMMENT THIS CODE IF BLUR SHOULD BE SUPPORTED
// const checkForError = (fieldMappings, $el) => {
//   const $activeElement = $(document.activeElement);
//   const fieldList = getFieldList(fieldMappings, $el);
//   const activeElementInList = find(fieldList, $field => $field.is($activeElement));
//   const el = $el[0];

//   if (activeElementInList && $el.is(FORM_FIELDS) && !el.validity.valid) {
//     return;
//   }

//   fieldList.forEach(($field) => {
//     $field.trigger('validator:validate');
//   });

//   const $error = find(fieldList, ($field) => {
//     if (!$field.is(FORM_FIELDS)) {
//       return false;
//     }

//     const field = $field[0];
//     field.checkValidity();
//     const validity = $field.prop('validity');
//     return !validity.valid;
//   });

//   if ($error) {
//     fieldList.forEach(($field) => {
//       if ($field.is(FORM_FIELDS)) {
//         const field = $field[0];
//         field.setCustomValidity($error.prop('validationMessage'));
//         field.checkValidity();
//       }
//     });
//   } else {
//     fieldList.forEach(($field) => {
//       if ($field.is(FORM_FIELDS)) {
//         const field = $field[0];
//         field.setCustomValidity('');
//         field.checkValidity();

//         const validEvt = new CustomEvent('valid');
//         field.dispatchEvent(validEvt);
//       }
//     });
//   }
// };

/**
 * Responsible for setting up mirror field groupings through exposing the
 * ability to register via a function or a jQuery plugin.
 * @return {Object} Available methods for interacting with mirror fields
 */
export default () => {
  let fieldMappings = {};

  const register = (el) => {
    const $el = $(el);
    const nextFieldMappings = updateFieldMappings(fieldMappings, $el);
    fieldMappings = nextFieldMappings;

    $el
      .on(createEventName('invalid'), ({ target }) => applyError(fieldMappings, $(target)))
      .on(createEventName('focus'), ({ target }) => resetError(fieldMappings, $(target)));

    // UNCOMMENT THIS CODE IF BLUR SHOULD BE SUPPORTED
    // $el.on(createEventName('blur'), ({ target }) => checkForError(fieldMappings, $(target)))

    // UNCOMMENT THIS CODE IF BLUR SHOULD BE SUPPORTED
    // if ($el.prop('tagName').toLowerCase() === 'select') {
    //   $el.on(createEventName('change'), ({ target }) => checkForError(fieldMappings, $(target)));
    // }

    return {
      destroy: () => $el.off(`.${EVENT_NAMESPACE}`),
    };
  };

  registerJQueryPlugin('mirrorField', register);

  return { register };
};


// WEBPACK FOOTER //
// ./styleguide/src/assets/elements/fields/mirror-field.js